import { http, HttpResponse } from 'msw';
import { viewsListData } from '../data/transactions/views';
import type {
  DashboardViewModel,
  DuplicateViewBody,
  ReorderViewsBody,
  RenameViewBody,
  SaveViewBody,
  UpdateViewBody
} from '~/types';

let nextMockViewId = 1;

const isNameTakenExcluding = (name: string, excludeId: string) =>
  viewsListData.some(
    (v) => v.Id !== excludeId && v.ViewContent.name.toLowerCase() === name.toLowerCase()
  );

export const viewsHandlers = [
  http.get('**/api/**/Views', () => {
    return HttpResponse.json(viewsListData);
  }),

  http.post('**/api/**/Views', async ({ request }) => {
    const body = (await request.json()) as SaveViewBody;
    if (
      viewsListData.some(
        (v) => v.ViewContent.name.toLowerCase() === body.ViewContent.name.toLowerCase()
      )
    ) {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }
    const created: DashboardViewModel = {
      Id: `created-view-${nextMockViewId++}`,
      UserId: 1,
      PartnerName: 'test001atosv2',
      TableIdentifier: body.TableIdentifier,
      IsDefault: body.IsDefault,
      Scope: 'own',
      Position: viewsListData.length,
      ViewContent: body.ViewContent
    };
    viewsListData.push(created);
    return HttpResponse.json(created);
  }),

  http.put('**/api/**/Views/reorder', async ({ request }) => {
    const body = (await request.json()) as ReorderViewsBody;
    const ownIds = viewsListData.filter((v) => v.Scope === 'own').map((v) => v.Id);
    const hasUnknownId = body.orderedIds.some((id) => !ownIds.includes(id));
    if (hasUnknownId) return HttpResponse.json({ code: 400 }, { status: 400 });

    body.orderedIds.forEach((id, index) => {
      const view = viewsListData.find((v) => v.Id === id);
      if (view) view.Position = index;
    });
    return new HttpResponse(null, { status: 204 });
  }),

  http.patch('**/api/**/Views/:id/name', async ({ params, request }) => {
    const id = String(params.id);
    const idx = viewsListData.findIndex((v) => v.Id === id);
    if (idx === -1) return HttpResponse.json({ code: 404 }, { status: 404 });
    if (viewsListData[idx].Scope !== 'own') {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }

    const body = (await request.json()) as RenameViewBody;
    if (isNameTakenExcluding(body.name, id)) {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }

    viewsListData[idx] = {
      ...viewsListData[idx],
      ViewContent: { ...viewsListData[idx].ViewContent, name: body.name }
    };
    return HttpResponse.json(viewsListData[idx]);
  }),

  http.post('**/api/**/Views/:id/duplicate', async ({ params, request }) => {
    const id = String(params.id);
    const source = viewsListData.find((v) => v.Id === id);
    if (!source) return HttpResponse.json({ code: 404 }, { status: 404 });

    const body = (await request.json()) as DuplicateViewBody;
    const name = body.name ?? `${source.ViewContent.name} (copy)`;
    if (isNameTakenExcluding(name, '')) {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }

    const duplicated: DashboardViewModel = {
      Id: `duplicated-view-${nextMockViewId++}`,
      UserId: 1,
      PartnerName: 'test001atosv2',
      TableIdentifier: source.TableIdentifier,
      IsDefault: false,
      Scope: 'own',
      Position: viewsListData.length,
      ViewContent: { ...source.ViewContent, name }
    };
    viewsListData.push(duplicated);
    return HttpResponse.json(duplicated, { status: 201 });
  }),

  http.patch('**/api/**/Views/:id/default', ({ params }) => {
    const id = String(params.id);
    const idx = viewsListData.findIndex((v) => v.Id === id);
    if (idx === -1) return HttpResponse.json({ code: 404 }, { status: 404 });
    if (viewsListData[idx].Scope !== 'own') {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }

    viewsListData.forEach((view) => {
      view.IsDefault = view.Id === id;
    });
    return HttpResponse.json(viewsListData[idx]);
  }),

  http.delete('**/api/**/Views/:id', ({ params }) => {
    const id = String(params.id);
    const idx = viewsListData.findIndex((v) => v.Id === id);
    if (idx === -1) return HttpResponse.json({ code: 404 }, { status: 404 });
    if (viewsListData[idx].Scope !== 'own') {
      return HttpResponse.json({ code: 422 }, { status: 422 });
    }

    viewsListData.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  http.put('**/api/**/Views/:id', async ({ params, request }) => {
    const id = String(params.id);
    const idx = viewsListData.findIndex((v) => v.Id === id);
    if (idx === -1) return HttpResponse.json({ code: 404 }, { status: 404 });

    const body = (await request.json()) as UpdateViewBody;
    if (isNameTakenExcluding(body.name, id)) {
      return HttpResponse.json({ code: 409 }, { status: 409 });
    }

    viewsListData[idx] = {
      ...viewsListData[idx],
      ViewContent: { ...body.viewContent, name: body.name }
    };
    return HttpResponse.json(viewsListData[idx]);
  })
];
